/* =====================================================================
 * @auw/tenant — Asset URL resolvers.
 *
 * Two layers live here:
 *
 *   1. `absoluteAssetUrl()` — the legacy SEO/OG joiner. Resolves a
 *      tenant-relative path into `${SITE_URL}${path}` for tags whose
 *      string output must keep rendering byte-for-byte identically
 *      (OG:image, canonical link, manifest icons).
 *
 *      Rules:
 *        - Already-absolute URLs (http/https) pass through unchanged.
 *        - Paths keep their leading slash; missing slashes are inserted.
 *        - Trailing slash on `siteUrl` is stripped before joining.
 *
 *   2. Optional-asset resolvers (`resolveWorkspaceModeAvatar`,
 *      `resolveSystemWallpaper`, `resolveSkillReferenceLogo`,
 *      `resolveAppNameLogo`) — a consistent API for components that
 *      branch on tenant-supplied visuals. They share three properties:
 *        - Pure (no side effects, safe inside `useMemo`).
 *        - Always return a usable value or an explicit `null`/
 *          provided fallback — the caller never has to re-implement
 *          the missing-asset branch.
 *        - Return raw `src` strings (NOT pre-joined with siteUrl),
 *          so they work with both `publicDir`-served paths
 *          (`/assets/foo.png`) and external CDNs.
 * ===================================================================== */

import type {
  TenantManifest,
  TenantSkillAssets,
  TenantSuggestionEntry,
  TenantWallpaperAssets,
  TenantWorkspaceModeAssetsMap,
} from './types';

const ABSOLUTE_URL_RE = /^https?:\/\//i;

export function absoluteAssetUrl(manifest: TenantManifest, path: string): string {
  if (ABSOLUTE_URL_RE.test(path)) return path;
  const base = manifest.identity.siteUrl.replace(/\/+$/, '');
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalized}`;
}

/**
 * Shape returned by image-with-alt resolvers. `alt` is left optional
 * because callers typically have a sensible default (the mode label,
 * the skill title) and only need the override when the tenant has
 * supplied one.
 */
export interface ResolvedAssetWithAlt {
  readonly src: string;
  readonly alt?: string;
}

/**
 * Sidebar workspace-mode avatar lookup.
 *
 * Returns the per-mode override declared under
 * `assets.workspaceModes.<mode>.avatar`, or `null` when the tenant
 * has not opted into a custom chip — the caller then renders the
 * legacy letter+colour-dot from `WORKSPACE_MODES`.
 */
export function resolveWorkspaceModeAvatar(
  manifest: TenantManifest,
  mode: keyof TenantWorkspaceModeAssetsMap,
): ResolvedAssetWithAlt | null {
  const override = manifest.assets.workspaceModes?.[mode];
  if (!override?.avatar) return null;
  return { src: override.avatar, alt: override.avatarAlt };
}

/**
 * Settings-modal system wallpaper lookup.
 *
 * Returns the tenant override for the requested theme, or the
 * caller-supplied `fallback` (typically the bundled
 * `${BASE_URL}assets/system_bg_*.webp`) when the tenant has not
 * customised the pair.
 */
export function resolveSystemWallpaper(
  manifest: TenantManifest,
  theme: 'dark' | 'light',
  fallback: string,
): string {
  const wallpapers: TenantWallpaperAssets | undefined =
    manifest.assets.wallpapers;
  if (!wallpapers) return fallback;
  const override = theme === 'dark' ? wallpapers.systemDark : wallpapers.systemLight;
  return override ?? fallback;
}

/**
 * Skill modal reference-card hero lookup.
 *
 * Tenants always render *some* image on the skill reference card, so
 * this resolver guarantees a non-null result by falling back to the
 * supplied `fallback` (typically the favicon path or a built-in
 * `/icons/...` asset). `alt` defers to the override when present and
 * is otherwise left undefined for the caller to populate from the
 * skill name.
 */
export function resolveSkillReferenceLogo(
  manifest: TenantManifest,
  fallback: string,
): ResolvedAssetWithAlt {
  const skills: TenantSkillAssets | undefined = manifest.assets.skills;
  if (!skills?.referenceCardLogo) return { src: fallback };
  return {
    src: skills.referenceCardLogo,
    alt: skills.referenceCardAlt,
  };
}

/**
 * App-name wordmark lookup with theme-aware variant selection.
 *
 * Resolution rules:
 *   - `assets.appNameLogo` is the source of truth for the light theme.
 *   - In `'dark'` theme, `assets.appNameLogoDark` is preferred when
 *     present.
 *   - In `'dark'` theme **without** a dark variant declared, the
 *     resolver returns `null` instead of falling through to the light
 *     variant — wordmarks are typically monochrome and rendering the
 *     light variant against a dark background hurts contrast more than
 *     hiding the mark altogether. Callers should treat `null` as
 *     "render no wordmark".
 *   - When `assets.appNameLogo` itself is unset (typical for on-prem
 *     tenants that ship a single SVG `agentLogo` instead of a separate
 *     wordmark), the resolver returns `null` for both themes.
 */
export function resolveAppNameLogo(
  manifest: TenantManifest,
  theme: 'dark' | 'light',
): ResolvedAssetWithAlt | null {
  const { appNameLogo, appNameLogoDark } = manifest.assets;
  if (!appNameLogo) return null;
  if (theme === 'dark') {
    return appNameLogoDark ? { src: appNameLogoDark } : null;
  }
  return { src: appNameLogo };
}

/**
 * Default values supplied by callers that own the agent-mode empty
 * state (today: `AgentContent.tsx`). The resolver fills any hole in
 * `manifest.chat.agentEmptyState` from this struct, so consumers
 * never need to repeat the legacy fallback chain.
 */
export interface AgentEmptyStateDefaults {
  readonly titlePrefix: string;
  readonly rotatingSuffixes: readonly string[];
  readonly description: string;
}

/**
 * Result of resolving `manifest.chat.agentEmptyState` against the
 * caller-supplied defaults. `suggestions === null` means "the tenant
 * did not opt into a custom suggestion list, so render the legacy
 * i18n-keyed default" — we deliberately do not fold the i18n default
 * into the resolver because i18n lives one layer above this package.
 * An empty array, on the other hand, is treated as an explicit
 * "render no suggestions" intent.
 */
export interface ResolvedAgentEmptyState {
  readonly titlePrefix: string;
  readonly rotatingSuffixes: readonly string[];
  readonly description: string;
  readonly suggestions: readonly TenantSuggestionEntry[] | null;
}

/**
 * Agent-mode empty-state lookup with caller-provided fallbacks.
 *
 * Resolution rules (per slot, all independent):
 *   - `titlePrefix` — manifest override → caller default.
 *   - `rotatingSuffixes` — manifest override (any non-undefined array,
 *     including empty) → caller default.
 *   - `description` — manifest override → caller default.
 *   - `suggestions` — manifest override (any non-undefined array,
 *     including empty) → `null` so the caller can branch on its own
 *     i18n-keyed legacy carousel.
 *
 * Empty arrays are honoured as explicit intent; only `undefined` /
 * missing keys trigger fallback. This matches how the rest of the
 * resolver family treats `appNameLogoDark`, `wallpapers`, etc.
 */
export function resolveAgentEmptyState(
  manifest: TenantManifest,
  defaults: AgentEmptyStateDefaults,
): ResolvedAgentEmptyState {
  const override = manifest.chat?.agentEmptyState;
  return {
    titlePrefix: override?.titlePrefix ?? defaults.titlePrefix,
    rotatingSuffixes: override?.rotatingSuffixes ?? defaults.rotatingSuffixes,
    description: override?.description ?? defaults.description,
    suggestions: override?.suggestions ?? null,
  };
}
